import type { ReactNode } from 'react';
import React from 'react';
import { Link } from 'react-router-dom';
import { GovEmpty } from '@gov-design-system-ce/react';
import type { ExtraConfig } from '@gov-nx/core/app';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import type { PageCode } from '@gov-nx/module/page';
import { useProfileEDocumentsContextInstance } from '@gov-nx/module/page';
import {
	Button,
	ContentLayout,
	DataTable,
	LayoutGap,
	PopConfirm,
} from '@gov-nx/ui/web';
import { toDateTimeReadable } from '@gov-nx/utils/common';
import { ProfileContentLayout } from '../profil-ROB/ProfileContentLayout';
import { ProfileEDocumentsSkeleton } from './ProfileEDocumentsSkeleton';

// Extract the type for easier extension
type ProfileEDocumentsViewConfig =
	ExtraConfig['profileRoutes']['eDokladyConfig']['profileEDocumentsView'];

export interface ProfileEDocumentsViewProps extends ProfileEDocumentsViewConfig {
	navigation: ReactNode;
	code: PageCode;
}

export const ProfileEDocumentsView = ({
	navigation,
	code,
	...ProfileEDocumentsViewConfig
}: ProfileEDocumentsViewProps) => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const ts = getLocalizeCurried(code);
	const { documents, initialLoading, onDeleteEDocument, isDeleting } =
		useProfileEDocumentsContextInstance();

	if (initialLoading) {
		return (
			<ContentLayout customClasses="pt-4!">
				{navigation}
				<ProfileEDocumentsSkeleton />
			</ContentLayout>
		);
	}

	return (
		<ProfileContentLayout {...ProfileEDocumentsViewConfig.profileContentLayout}>
			{navigation}
			{Array.isArray(documents) && documents.length ? (
				<LayoutGap
					gap="none"
					className="mt-(--templates-margin-l)!">
					{documents.map((document) => (
						<div
							key={document.id}
							className="p-(--templates-margin-l)">
							<div className="flex items-center justify-between mb-(--spacing-l)!">
								<h2 className="text-headline-s!">{document.nazev}</h2>

								<PopConfirm
									onConfirm={(closePrompt) =>
										document.kod && onDeleteEDocument(document.kod, closePrompt)
									}
									strings={{
										title: ts('odpojit.titulek'),
										body: ts(
											'odpojit.text',
											document.nazev ? { nazev: document.nazev } : {}
										),
										buttons: {
											cancel: {
												label: ts('odpojit.zrusit'),
												wcagLabel: ts('odpojit.zrusit'),
											},
											confirm: {
												label: ts('odpojit.potvrdit'),
												wcagLabel: ts('odpojit.potvrdit'),
											},
										},
									}}
									isDisabled={isDeleting}
									isLoading={isDeleting}>
									{({ open }) => (
										<Button
											onClick={open}
											color="primary"
											type="base"
											size="s"
											aria-label={ts('akce.odpojit')}>
											{ts('akce.odpojit')}
										</Button>
									)}
								</PopConfirm>
							</div>

							<div className="gov-table">
								<DataTable responsive>
									<thead>
										<tr>
											<th>{ts('tabulka.typ')}</th>
											<th>{ts('tabulka.aktualizace')}</th>
										</tr>
									</thead>
									<tbody>
										{document.edoklady?.map((edoklad) => (
											<tr key={edoklad.id}>
												<td data-label={ts('tabulka.typ')}>
													<strong>
														{ts(`tabulka.klic.${edoklad.typKod}`)}
													</strong>
												</td>
												<td data-label={ts('tabulka.aktualizace')}>
													{edoklad.datumAktualizace &&
														toDateTimeReadable(edoklad.datumAktualizace)}
												</td>
											</tr>
										))}
									</tbody>
								</DataTable>
							</div>
						</div>
					))}
				</LayoutGap>
			) : (
				<GovEmpty
					headline={null}
					size="l"
					align="center">
					<div className="flex flex-col items-center gap-(--spacing-xl)">
						<svg
							width="86"
							height="86"
							xmlns="http://www.w3.org/2000/svg"
							className="mx-auto">
							<image
								href="/assets/logos/edoklady.svg"
								height="86"
								width="86"
							/>
						</svg>
						<h2 className="text-headline-m! text-(--text-primary)!">
							{ts('banner.nova-era-prokazovani')}
						</h2>
					</div>
					<p className="text-center">{ts('banner.popis')}</p>
					<Button
						color="primary"
						type="solid"
						size="l"
						asChild>
						<Link
							target="_blank"
							to={ts('banner.zjistit-vice-url')}>
							{ts('banner.zjistit-vice')}
						</Link>
					</Button>
				</GovEmpty>
			)}
		</ProfileContentLayout>
	);
};
